import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Tag, Percent, DollarSign, Truck, Copy, Check, PowerOff, Power } from 'lucide-react';

const API = window.location.origin + '/api';

const TYPE_META = {
  percent:       { label: 'Percent off',   icon: Percent,     color: '#7C3AED', bg: '#EDE9FE' },
  fixed:         { label: 'Fixed amount',  icon: DollarSign,  color: '#065F46', bg: '#D1FAE5' },
  free_delivery: { label: 'Free delivery', icon: Truck,       color: '#0369A1', bg: '#E0F2FE' },
};

const EMPTY = {
  code: '', description: '', type: 'percent', value: 10, min_order_amount: 0,
  max_uses: '', max_uses_per_user: '', valid_from: '', valid_until: '', supermarket_id: '', active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [supermarkets, setSupermarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const navigate = useNavigate();

  const load = () => {
    axios.get(`${API}/admin/coupons`, { withCredentials: true })
      .then(r => setCoupons(r.data.coupons || []))
      .catch(err => console.error('Failed to load coupons:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    axios.get(`${API}/admin/supermarkets`, { withCredentials: true })
      .then(r => setSupermarkets(r.data))
      .catch(err => console.error('Failed to load supermarkets:', err));
  }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code: form.code.trim().toUpperCase(),
        description: form.description.trim(),
        type: form.type,
        value: Number(form.value),
        min_order_amount: Number(form.min_order_amount || 0),
        max_uses: form.max_uses === '' ? null : Number(form.max_uses),
        max_uses_per_user: form.max_uses_per_user === '' ? null : Number(form.max_uses_per_user),
        valid_from: form.valid_from,
        valid_until: form.valid_until,
        supermarket_id: form.supermarket_id,
        active: form.active,
      }, { withCredentials: true });
      setForm(EMPTY);
      setShowCreate(false);
      load();
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Could not create coupon');
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/admin/coupons/${c.id}`, { active: !c.active }, { withCredentials: true });
      setCoupons(prev => prev.map(x => x.id === c.id ? { ...x, active: !c.active } : x));
    } catch (err) {
      alert('Could not update coupon');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`, { withCredentials: true });
      setCoupons(prev => prev.filter(x => x.id !== c.id));
    } catch (err) {
      alert('Could not delete coupon');
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <div className="admin-page" data-testid="admin-coupons-page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Tag size={22} /> Coupon Codes
        </h2>
        <button
          onClick={() => { setShowCreate(true); setCreateError(''); }}
          data-testid="new-coupon-btn"
          style={{ padding: '9px 14px', background: 'var(--lime)', color: 'var(--navy)', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={15} /> New coupon
        </button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 20px' }}>
        Create promo codes customers can enter at checkout. Percent-off, fixed-amount, or free delivery.
      </p>

      {showCreate && (
        <form onSubmit={submitCreate} style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20, marginBottom: 20 }} data-testid="new-coupon-form">
          <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: 'var(--navy)' }}>New coupon</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Code*</span>
              <input required value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME10"
                data-testid="cf-code" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Type*</span>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} data-testid="cf-type" style={inputStyle}>
                <option value="percent">Percent off</option>
                <option value="fixed">Fixed amount off</option>
                <option value="free_delivery">Free delivery</option>
              </select>
            </label>
            {form.type !== 'free_delivery' && (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>
                  Value* {form.type === 'percent' ? '(%)' : '(K)'}
                </span>
                <input required type="number" min="0" step="0.01" value={form.value}
                  onChange={e => setForm({ ...form, value: e.target.value })} data-testid="cf-value" style={inputStyle} />
              </label>
            )}
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Min order (K)</span>
              <input type="number" min="0" step="0.01" value={form.min_order_amount}
                onChange={e => setForm({ ...form, min_order_amount: e.target.value })} data-testid="cf-min" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Max total uses</span>
              <input type="number" min="0" placeholder="unlimited" value={form.max_uses}
                onChange={e => setForm({ ...form, max_uses: e.target.value })} data-testid="cf-maxuses" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Max uses per customer</span>
              <input type="number" min="0" placeholder="unlimited" value={form.max_uses_per_user}
                onChange={e => setForm({ ...form, max_uses_per_user: e.target.value })} data-testid="cf-maxperuser" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Valid from</span>
              <input type="date" value={form.valid_from} onChange={e => setForm({ ...form, valid_from: e.target.value })} data-testid="cf-from" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Valid until</span>
              <input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} data-testid="cf-until" style={inputStyle} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Restrict to supermarket</span>
              <select value={form.supermarket_id} onChange={e => setForm({ ...form, supermarket_id: e.target.value })} data-testid="cf-store" style={inputStyle}>
                <option value="">All stores</option>
                {supermarkets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
          </div>
          <label style={{ display: 'block', marginTop: 10 }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569' }}>Description (shown internally only)</span>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="e.g. Welcome discount for new customers"
              data-testid="cf-desc" style={{ ...inputStyle, marginTop: 4 }} />
          </label>
          {createError && <div style={{ marginTop: 10, padding: 8, background: '#FEF2F2', color: '#991B1B', borderRadius: 6, fontSize: '0.82rem' }}>{createError}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button type="submit" disabled={creating} data-testid="cf-submit" style={{ padding: '9px 18px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', opacity: creating ? 0.6 : 1 }}>
              {creating ? 'Creating…' : 'Create coupon'}
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY); }} style={{ padding: '9px 16px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-gray)', borderRadius: 8, fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading coupons…</div>}
      {!loading && coupons.length === 0 && (
        <div data-testid="coupons-empty" style={{ textAlign: 'center', padding: '50px 20px', background: 'white', borderRadius: 12, border: '1px dashed var(--border-gray)', color: 'var(--text-muted)' }}>
          <Tag size={30} style={{ marginBottom: 8, opacity: 0.4 }} />
          <p style={{ margin: 0 }}>No coupons yet. Create your first promo code.</p>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {coupons.map(c => {
          const meta = TYPE_META[c.type] || TYPE_META.percent;
          const Icon = meta.icon;
          const storeName = supermarkets.find(s => s.id === c.supermarket_id)?.name;
          const usage = c.max_uses ? `${c.used_count}/${c.max_uses}` : `${c.used_count} used`;
          return (
            <div
              key={c.id}
              data-testid={`coupon-card-${c.id}`}
              style={{
                background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12,
                padding: 16, opacity: c.active ? 1 : 0.6,
                borderLeft: `4px solid ${meta.color}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                  <div style={{ background: meta.bg, color: meta.color, width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={18} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <code style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: 4, fontSize: '0.95rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.05em' }}>
                        {c.code}
                      </code>
                      <button onClick={() => copyCode(c.code)} data-testid={`coupon-copy-${c.id}`}
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                        {copiedCode === c.code ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                      {!c.active && <span style={{ background: '#FEE2E2', color: '#991B1B', padding: '1px 8px', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>Paused</span>}
                    </div>
                    {c.description && <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>{c.description}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexShrink: 0 }}>
                  <button onClick={() => toggleActive(c)} data-testid={`coupon-toggle-${c.id}`}
                    style={{ padding: '6px 10px', background: c.active ? '#FEF3C7' : '#D1FAE5', color: c.active ? '#92400E' : '#065F46', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {c.active ? <><PowerOff size={12} /> Pause</> : <><Power size={12} /> Resume</>}
                  </button>
                  <button onClick={() => remove(c)} data-testid={`coupon-delete-${c.id}`}
                    style={{ padding: '6px 10px', background: '#FEE2E2', color: '#991B1B', border: 'none', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.78rem', color: '#475569' }}>
                <div><strong>Type:</strong> {meta.label}{c.type === 'percent' ? ` (${c.value}%)` : c.type === 'fixed' ? ` (K${c.value})` : ''}</div>
                {c.min_order_amount > 0 && <div><strong>Min order:</strong> K{c.min_order_amount}</div>}
                <div><strong>Uses:</strong> {usage}</div>
                {c.max_uses_per_user && <div><strong>Per customer:</strong> {c.max_uses_per_user}</div>}
                {(c.valid_from || c.valid_until) && (
                  <div><strong>Valid:</strong> {c.valid_from || 'now'} → {c.valid_until || 'no end'}</div>
                )}
                {storeName && <div><strong>Store:</strong> {storeName}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-gray)',
  fontSize: '0.88rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};
